import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { colors } from '../theme';

const models = ['SC-8KW-20KWH', 'SC-10KW-20KWH', 'SC-12KW-20KWH'];

// [label, value-8kW, value-10kW, value-12kW] — a single value spans all models
const inverterRows = [
  ['Rated Power (kW)', '8', '10', '12'],
  ['Max Output Apparent Power (kVA)', '8.8', '11', '13.2'],
  ['AC Output Voltage', '400V (±2%), 3 Phase'],
  ['Maximum Output AC Current (A)', '13.3', '16.7', '20'],
  ['Maximum PV Input Voltage', '1000V'],
  ['MPPT Operating Range', '200–800V, 2 MPPT'],
  ['Efficiency', '98% (PV), 94.5% (Battery); 97.5% Europe'],
  ['Communication', 'RS485 / CAN / WiFi'],
  ['Inverter IP Protection', 'IP66'],
];

const batteryRows = [
  ['Battery Type', 'LFP (Lithium Iron Phosphate)'],
  ['Rated Capacity', '20kWh (10 / 40 / 60kWh optional)'],
  ['Rated Voltage', '51.2V'],
  ['Max Continuous Charge/Discharge', '240A'],
  ['BMS Protection', 'Max 300A continuous, active balancing, 5 NTC temperature sensors, Bluetooth monitoring, 4" colour LCD'],
  ['Fast DC Fuse Protection', 'Yes — overall and each compartment'],
  ['Fire Suppression', '5x automatic fire extinguishers, smoke sensor & gas detection optional'],
  ['Enclosure', 'W700 x D325 x H1380mm, 180kg, powder-coated steel, IP65, 2-hour fire protection option'],
  ['Operating Conditions', '0–65°C, ≤95% humidity, air cooling'],
];

function SpecRows({ rows }) {
  return rows.map((row) => (
    <TableRow key={row[0]} sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell
        sx={{ fontWeight: 600, color: colors.black, fontSize: '0.85rem', minWidth: 180 }}
      >
        {row[0]}
      </TableCell>
      {row.length === 2 ? (
        <TableCell colSpan={3} align="center" sx={{ color: '#555', fontSize: '0.85rem' }}>
          {row[1]}
        </TableCell>
      ) : (
        row.slice(1).map((value, i) => (
          <TableCell key={i} align="center" sx={{ color: '#555', fontSize: '0.85rem' }}>
            {value}
          </TableCell>
        ))
      )}
    </TableRow>
  ));
}

function BessSpecTable() {
  return (
    <Box sx={{ mt: 5, maxWidth: 900, mx: 'auto' }}>
      <Accordion
        elevation={0}
        sx={{
          borderRadius: '16px !important',
          border: `2px solid ${colors.green}40`,
          overflow: 'hidden',
          '&::before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: colors.green }} />}
          sx={{ px: 3, py: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: colors.black }}>
            B-ESS Full Technical Data{' '}
            <Box component="span" sx={{ color: colors.green }}>
              — 3 Models
            </Box>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: colors.green }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                    B-ESS MODEL
                  </TableCell>
                  {models.map((model) => (
                    <TableCell
                      key={model}
                      align="center"
                      sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}
                    >
                      {model}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{ bgcolor: `${colors.yellow}20`, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}
                  >
                    HYBRID INVERTER
                  </TableCell>
                </TableRow>
                <SpecRows rows={inverterRows} />
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{ bgcolor: `${colors.yellow}20`, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}
                  >
                    BATTERY & SAFETY
                  </TableCell>
                </TableRow>
                <SpecRows rows={batteryRows} />
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              bgcolor: `${colors.green}0D`,
            }}
          >
            <LocalFireDepartmentIcon sx={{ color: '#E53935', fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: colors.darkGreen, textAlign: 'center' }}>
              Designed to comply with SCDF fire safety requirements for roof and
              basement installations — Made in Singapore
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default BessSpecTable;
